import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Button, Card, Col, Modal, Row, Spinner } from "react-bootstrap";
import { List } from "../../component/list/list";
import { api } from "../../services/apis/configs/axiosConfigs";
import { API_URI } from "../../constants/consts";

type ReportItem = {
	id: string;
	questionId: string;
	questionNumber: string;
	year: number | null;
	discipline: string;
	reportedBy: string;
	createdAt: string;
};

type ReportDetail = {
	id: string;
	description: string;
	status: string;
	createdAt: string;
	reportedBy: {
		id: string | null;
		name: string;
	};
	question: {
		id: string | null;
		number: string;
		year: number | null;
		discipline: string;
		source: string | null;
		externalQuestionId: string | null;
		statement: string | null;
	};
};

const UNESP_PDF_PATHS: Record<number, string> = {
	2022: "/var/www/html/data/pdfs/inep_enem/UNESP2022_1fase_prova.pdf",
	2023: "/var/www/html/data/provas_faltantes_2023_2026/UNESP2023_1fase_prova.pdf",
	2024: "/var/www/html/data/provas_faltantes_2023_2026/UNESP2024_2_1fase_prova.pdf",
	2025: "/var/www/html/data/provas_faltantes_2023_2026/UNESP2025_2_1fase_prova.pdf",
	2026: "/var/www/html/data/provas_faltantes_2023_2026/UNESP2026_1fase_prova.pdf",
};

const extractFirstImageSrc = (html?: string | null): string | null => {
	if (!html) return null;

	const wrapper = document.createElement("div");
	wrapper.innerHTML = String(html);
	const firstImage = wrapper.querySelector("img");

	return firstImage?.getAttribute("src") || null;
};

const normalizeImageUrl = (imageSrc: string): string => {
	if (imageSrc.startsWith("http://") || imageSrc.startsWith("https://")) {
		return imageSrc;
	}

	if (imageSrc.startsWith("//")) {
		return `${window.location.protocol}${imageSrc}`;
	}

	const apiOrigin = API_URI ? new URL(API_URI).origin : window.location.origin;
	return new URL(imageSrc, apiOrigin).toString();
};

async function fetchQuestionPhoto(detail: ReportDetail, token: string) {
	const question = detail.question;

	if (question?.source === "unesp-pdf-extractor" && question?.externalQuestionId) {
		const match = question.externalQuestionId.match(/unesp-1fase-(\d{4})-q(\d+)/);
		if (match) {
			const year = parseInt(match[1], 10);
			const questionNumber = parseInt(match[2], 10);
			const pdfPath = UNESP_PDF_PATHS[year];

			if (!pdfPath) {
				return { photoUrl: null, photoBase64: null, error: `PDF da UNESP ${year} não disponível.` };
			}

			const response = await api.request({
				url: `${API_URI}/admin/private/questions/process-image`,
				method: "POST",
				headers: { Authorization: `Bearer ${token}` },
				data: { pdf_path: pdfPath, question_number: questionNumber },
			});

			const result = response.data?.data ?? response.data;
			return {
				photoUrl: result?.figura_url ?? null,
				photoBase64: result?.figura_base64 ?? null,
				error: result?.figura_url || result?.figura_base64 ? "" : "Imagem não encontrada para esta questão no PDF.",
			};
		}
	}

	const embeddedImage = extractFirstImageSrc(question?.statement);
	if (!embeddedImage) {
		return { photoUrl: null, photoBase64: null, error: "Nenhuma imagem disponível para esta questão." };
	}

	if (embeddedImage.startsWith("data:image/")) {
		return {
			photoUrl: null,
			photoBase64: embeddedImage.split(",")[1] ?? null,
			error: "",
		};
	}

	return {
		photoUrl: normalizeImageUrl(embeddedImage),
		photoBase64: null,
		error: "",
	};
}

function ReportDetailModal({
	show,
	onHide,
	token,
	reportId,
}: {
	show: boolean;
	onHide: () => void;
	token: string;
	reportId: string | null;
}) {
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState("");
	const [detail, setDetail] = useState<ReportDetail | null>(null);
	const [photoLoading, setPhotoLoading] = useState(false);
	const [photoError, setPhotoError] = useState("");
	const [photoUrl, setPhotoUrl] = useState<string | null>(null);
	const [photoBase64, setPhotoBase64] = useState<string | null>(null);

	useEffect(() => {
		if (!show || !reportId) {
			setLoading(false);
			setError("");
			setDetail(null);
			setPhotoLoading(false);
			setPhotoError("");
			setPhotoUrl(null);
			setPhotoBase64(null);
			return;
		}

		const loadReport = async () => {
			setLoading(true);
			setError("");
			setPhotoError("");
			setPhotoUrl(null);
			setPhotoBase64(null);

			try {
				const response = await axios.get(
					`${process.env.REACT_APP_API_URI}/admin/private/question-reports/${reportId}`,
					{
						headers: { Authorization: `Bearer ${token}` },
					}
				);

				const reportData: ReportDetail = response.data?.data ?? response.data;
				setDetail(reportData);

				setPhotoLoading(true);
				try {
					const photoResult = await fetchQuestionPhoto(reportData, token);
					setPhotoUrl(photoResult.photoUrl);
					setPhotoBase64(photoResult.photoBase64);
					setPhotoError(photoResult.error);
				} catch {
					setPhotoError("Erro ao carregar a foto da questão.");
				} finally {
					setPhotoLoading(false);
				}
			} catch (fetchError: any) {
				setError(
					fetchError?.response?.data?.message ||
					"Não foi possível carregar os detalhes do report."
				);
			} finally {
				setLoading(false);
			}
		};

		loadReport();
	}, [reportId, show, token]);

	return (
		<Modal show={show} onHide={onHide} size="xl" centered>
			<Modal.Header closeButton>
				<Modal.Title>Detalhes do Erro Reportado</Modal.Title>
			</Modal.Header>
			<Modal.Body>
				{loading ? (
					<div className="text-center py-4">
						<Spinner animation="border" />
					</div>
				) : error ? (
					<div className="alert alert-danger mb-0">{error}</div>
				) : detail ? (
					<>
						<Row className="g-4 mb-3">
							<Col md={3}>
								<Card className="border-0 shadow-sm h-100">
									<Card.Body>
										<div className="text-muted small">Número da Questão</div>
										<div style={{ fontWeight: 600 }}>{detail.question.number || "-"}</div>
									</Card.Body>
								</Card>
							</Col>
							<Col md={3}>
								<Card className="border-0 shadow-sm h-100">
									<Card.Body>
										<div className="text-muted small">Ano</div>
										<div style={{ fontWeight: 600 }}>{detail.question.year ?? "-"}</div>
									</Card.Body>
								</Card>
							</Col>
							<Col md={3}>
								<Card className="border-0 shadow-sm h-100">
									<Card.Body>
										<div className="text-muted small">Disciplina</div>
										<div style={{ fontWeight: 600 }}>{detail.question.discipline || "-"}</div>
									</Card.Body>
								</Card>
							</Col>
							<Col md={3}>
								<Card className="border-0 shadow-sm h-100">
									<Card.Body>
										<div className="text-muted small">Quem Reportou</div>
										<div style={{ fontWeight: 600 }}>{detail.reportedBy.name || "-"}</div>
									</Card.Body>
								</Card>
							</Col>
						</Row>

						<Card className="border-0 shadow-sm mb-4">
							<Card.Header>Texto Reportado</Card.Header>
							<Card.Body>
								<div style={{ whiteSpace: "pre-wrap" }}>{detail.description}</div>
							</Card.Body>
						</Card>

						<Card className="border-0 shadow-sm">
							<Card.Header>Foto da Questão (PDF)</Card.Header>
							<Card.Body>
								{photoLoading ? (
									<div className="text-center py-4">
										<Spinner animation="border" />
									</div>
								) : photoUrl || photoBase64 ? (
									<img
										src={photoUrl ?? `data:image/png;base64,${photoBase64}`}
										alt="Foto da questão reportada"
										style={{ maxWidth: "100%", height: "auto", borderRadius: "0.25rem" }}
									/>
								) : (
									<div className={photoError ? "alert alert-warning mb-0" : "text-muted"}>
										{photoError || "Nenhuma imagem disponível para esta questão."}
									</div>
								)}
							</Card.Body>
						</Card>
					</>
				) : (
					<div className="alert alert-info mb-0">Nenhum detalhe disponível.</div>
				)}
			</Modal.Body>
			<Modal.Footer>
				<Button variant="outline-secondary" onClick={onHide}>
					Fechar
				</Button>
			</Modal.Footer>
		</Modal>
	);
}

export function QuestionReportsView() {
	const navigate = useNavigate();
	const token = useSelector((state: any) => state.auth.token) || localStorage.getItem("token") || "";
	const [reports, setReports] = useState<ReportItem[]>([]);
	const [loading, setLoading] = useState(false);
	const [search, setSearch] = useState("");
	const [searchTimeout, setSearchTimeout] = useState<ReturnType<typeof setTimeout> | null>(null);
	const [currentPage, setCurrentPage] = useState(1);
	const [lastPage, setLastPage] = useState(1);
	const [selectedReportId, setSelectedReportId] = useState<string | null>(null);
	const [showDetailModal, setShowDetailModal] = useState(false);

	const fetchReports = useCallback(
		async (page: number, searchQuery: string) => {
			setLoading(true);
			try {
				const response = await axios.get(
					`${process.env.REACT_APP_API_URI}/admin/private/question-reports`,
					{
						headers: { Authorization: `Bearer ${token}` },
						params: {
							page,
							search: searchQuery || undefined,
						},
					}
				);

				const payload = response.data?.data ?? response.data;
				setReports(payload?.reports ?? []);
				setLastPage(payload?.pagination?.totalPages ?? 1);
			} catch (error) {
				console.error("Error fetching question reports:", error);
				setReports([]);
				setLastPage(1);
			} finally {
				setLoading(false);
			}
		},
		[token]
	);

	useEffect(() => {
		if (!search.trim()) {
			fetchReports(currentPage, "");
		}
	}, [currentPage, fetchReports, search]);

	const handleSearch = (value: string) => {
		setSearch(value);
		setCurrentPage(1);

		if (searchTimeout) {
			clearTimeout(searchTimeout);
		}

		if (value.trim() === "") {
			fetchReports(1, "");
			return;
		}

		const timeoutId = setTimeout(() => {
			fetchReports(1, value);
		}, 500);

		setSearchTimeout(timeoutId);
	};

	const openDetailModal = (reportId: string) => {
		setSelectedReportId(reportId);
		setShowDetailModal(true);
	};

	const openCorrectionForm = (reportId: string) => {
		navigate(`/questions/correct-report/${reportId}`);
	};

	return (
		<div className="row">
			<List
				onChangeSearchInput={handleSearch}
				search_input_value={search}
				title="Erros Reportados"
				search_text="Pesquisar por número, ano, disciplina, reportante ou texto"
				not_found_message="Nenhum erro reportado encontrado"
				onChangePagination={(value: number) => setCurrentPage(value)}
				show_warning={false}
				deleting_message=""
				loading_screen={loading}
				loading_search={loading}
				items={reports}
				pagination={{ last_page: lastPage, current_page: currentPage }}
				fields={[
					{
						header: "Número da Questão",
						name: "questionNumber",
						type: "custom",
						render: (item: ReportItem) => (
							<button
								type="button"
								className="btn btn-link p-0"
								onClick={() => openDetailModal(item.id)}
							>
								{item.questionNumber || "-"}
							</button>
						),
					},
					{ header: "Ano", name: "year", type: "text" },
					{ header: "Disciplina", name: "discipline", type: "text" },
					{ header: "Quem Reportou", name: "reportedBy", type: "text" },
					{ header: "Data que foi reportada", name: "createdAt", type: "date" },
					{
						header: "Ações",
						name: "id",
						type: "custom",
						render: (item: ReportItem) => (
							<Button
								variant="outline-primary"
								size="sm"
								onClick={() => openCorrectionForm(item.id)}
							>
								Corrigir
							</Button>
						),
					},
				]}
				onSearchButtonClick={() => undefined}
				hideManager={true}
				hideEdit={true}
				hideDelete={true}
			/>

			<ReportDetailModal
				show={showDetailModal}
				onHide={() => {
					setShowDetailModal(false);
					setSelectedReportId(null);
				}}
				token={token}
				reportId={selectedReportId}
			/>
		</div>
	);
}

export default QuestionReportsView;